import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function TermsOfServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#0A1628] text-white">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://static.readdy.ai/image/d6b4b88b420755e3ba36fdd8ab8e8672/43b1a7a9dc913fdbd622cad5f4bf97fa.png"
              alt="Tri County Ninja"
              className="h-12 w-auto"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center text-white/80 hover:text-[#FF6B35] transition-colors duration-300 text-sm font-medium cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-[#0A1628] text-white pb-16 pt-10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF6B35]/20 rounded-full mb-6">
            <i className="ri-file-text-line text-[#FF6B35] text-3xl"></i>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
          <p className="text-white/60 text-sm">Last updated: April 15, 2026</p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="prose max-w-none">

          <section className="mb-12">
            <p className="text-gray-600 text-base leading-relaxed">
              Welcome to Tri County Ninja. By accessing our website or using our facility and services at 14283 Fenton Rd, Fenton, MI 48430, you agree to be bound by these Terms of Service. Please read them carefully before participating in any programs or activities. If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">1</span>
              Acceptance of Terms
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                By registering for classes, purchasing a membership, or entering our facility, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and all applicable laws and regulations. These terms apply to all visitors, members, and participants, including parents or guardians acting on behalf of minors.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">2</span>
              Memberships &amp; Registrations
            </h2>
            <div className="pl-11">
              <ul className="list-none space-y-3">
                {[
                  'All memberships and class registrations are personal and non-transferable.',
                  'Monthly memberships automatically renew unless cancelled at least 7 days before the next billing cycle.',
                  'Drop-in passes and class packs are non-refundable once purchased.',
                  'Tri County Ninja reserves the right to modify pricing with 30 days\' written notice to active members.',
                  'Members must maintain a valid payment method on file at all times.',
                  'Accounts with outstanding balances may be suspended until payment is received.',
                ].map((item) => (
                  <li key={item} className="flex items-start text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#FF6B35] mr-3 mt-0.5 shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">3</span>
              Assumption of Risk &amp; Waiver
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 mb-4 leading-relaxed">
                Participation in ninja obstacle training, gymnastics, and related activities involves inherent risks of physical injury. By participating, you voluntarily assume all risks associated with these activities, including but not limited to:
              </p>
              <ul className="list-none space-y-2 mb-4">
                {[
                  'Falls, collisions, and contact with equipment or other participants',
                  'Muscle strains, sprains, fractures, or other physical injuries',
                  'Injuries resulting from your own actions or the actions of others',
                ].map((item) => (
                  <li key={item} className="flex items-start text-gray-600">
                    <i className="ri-error-warning-line text-[#FF6B35] mr-3 mt-0.5 shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-gray-600 leading-relaxed">
                All participants (or their parent/guardian if under 18) must sign a separate liability waiver before participating in any activity at our facility. Failure to sign the waiver will result in denial of participation.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">4</span>
              Code of Conduct
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 mb-4 leading-relaxed">All participants and guests are expected to:</p>
              <ul className="list-none space-y-2">
                {[
                  'Treat staff, coaches, and fellow participants with respect at all times',
                  'Follow all instructions given by coaches and staff',
                  'Wear appropriate athletic attire and footwear (or go barefoot as directed)',
                  'Refrain from using foul language, bullying, or aggressive behavior',
                  'Not use equipment without proper instruction or supervision',
                  'Report any injuries or unsafe conditions to staff immediately',
                  'Supervise children at all times when not in a structured class',
                ].map((item) => (
                  <li key={item} className="flex items-start text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#FF6B35] mr-3 mt-0.5 shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-gray-600 mt-4 leading-relaxed">
                Tri County Ninja reserves the right to remove any individual from the facility for violating this code of conduct without refund.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">5</span>
              Cancellations &amp; Refunds
            </h2>
            <div className="pl-11">
              <ul className="list-none space-y-3">
                {[
                  'Class cancellations must be made at least 24 hours in advance to receive a make-up credit.',
                  'No-shows and late cancellations (less than 24 hours) will forfeit the class session.',
                  'Birthday party and event bookings require a non-refundable deposit. Cancellations made more than 14 days before the event may receive a partial refund of remaining fees.',
                  'Membership cancellations must be submitted in writing via email to staff@tricountyninja.com.',
                  'Tri County Ninja is not responsible for refunds due to personal schedule changes, illness, or weather unless the facility is officially closed.',
                ].map((item) => (
                  <li key={item} className="flex items-start text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#FF6B35] mr-3 mt-0.5 shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">6</span>
              Health &amp; Safety Requirements
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 mb-4 leading-relaxed">For the safety of all participants:</p>
              <ul className="list-none space-y-2">
                {[
                  'Participants must disclose any medical conditions, injuries, or physical limitations prior to participation.',
                  'Participants who are ill or showing symptoms of contagious illness must not attend classes or open gym.',
                  'Tri County Ninja staff may refuse participation to anyone who appears unfit to safely participate.',
                  'All participants must follow any posted safety rules and equipment guidelines.',
                ].map((item) => (
                  <li key={item} className="flex items-start text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#FF6B35] mr-3 mt-0.5 shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">7</span>
              Photography &amp; Media
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                Tri County Ninja may photograph or video record classes, events, and open gym sessions for promotional and social media purposes. By participating, you grant us permission to use your likeness (or your child&apos;s likeness) in such materials unless you provide written notice to the contrary. If you do not wish to be photographed, please notify staff before your session.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">8</span>
              Limitation of Liability
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                To the fullest extent permitted by law, Tri County Ninja, its owners, employees, coaches, and agents shall not be liable for any direct, indirect, incidental, special, or consequential damages arising from your use of our facility or services, including personal injury, property damage, or loss of any kind. Our total liability to you for any claim shall not exceed the amount paid by you for the specific service giving rise to the claim.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">9</span>
              Governing Law
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                These Terms of Service shall be governed by and construed in accordance with the laws of the State of Michigan, without regard to its conflict of law provisions. Any disputes arising under these terms shall be resolved exclusively in the courts located in Genesee County, Michigan.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">10</span>
              Changes to These Terms
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after any changes constitutes your acceptance of the new terms. We encourage you to review these terms periodically.
              </p>
            </div>
          </section>

          {/* Contact Box */}
          <div className="bg-[#0A1628] rounded-2xl p-8 text-white">
            <h2 className="text-xl font-bold mb-3">Questions About These Terms?</h2>
            <p className="text-white/70 mb-6 text-sm leading-relaxed">
              If you have any questions about these Terms of Service, please reach out to us:
            </p>
            <div className="space-y-3">
              <div className="flex items-center text-sm">
                <i className="ri-map-pin-line text-[#FF6B35] mr-3 text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="text-white/80">14283 Fenton Rd, Fenton, MI 48430</span>
              </div>
              <div className="flex items-center text-sm">
                <i className="ri-phone-line text-[#FF6B35] mr-3 text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="text-white/80">(810) 354-8352</span>
              </div>
              <div className="flex items-center text-sm">
                <i className="ri-mail-line text-[#FF6B35] mr-3 text-lg w-5 h-5 flex items-center justify-center"></i>
                <a href="mailto:staff@tricountyninja.com" className="text-white/80 hover:text-[#FF6B35] transition-colors">staff@tricountyninja.com</a>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0A1628] text-white mt-0">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">© 2025 Tri County Ninja. All rights reserved.</p>
            <div className="flex space-x-6 text-sm flex-wrap justify-center gap-y-2">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-[#FF6B35] transition-colors cursor-pointer whitespace-nowrap">Privacy Policy</Link>
              <span className="text-gray-600">|</span>
              <Link to="/terms-of-service" className="text-[#FF6B35] transition-colors cursor-pointer whitespace-nowrap">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
